import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import api from '../lib/api';
import { 
    ArrowRight, 
    Building2, 
    GraduationCap, 
    Users, 
    Briefcase,
    ChevronRight,
    Map,
    Shield,
    TrendingUp,
    DollarSign,
    Target,
    Handshake,
    BarChart3,
    Globe2,
    BookOpen,
    Lock,
    FolderSearch,
    UserCheck,
    Truck,
    Heart
} from 'lucide-react';

const HomePage = () => {
    const [impactData, setImpactData] = useState(null);
    const [chapters, setChapters] = useState([]);
    const [partners, setPartners] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [chaptersRes, impactRes, partnersRes] = await Promise.all([
                    api.get('/chapters'),
                    api.get('/impact/dashboard').catch(() => ({ data: null })),
                    api.get('/partner-databases').catch(() => ({ data: [] }))
                ]);
                setChapters(chaptersRes.data);
                setImpactData(impactRes.data);
                setPartners(partnersRes.data);
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const memberCategories = [
        { icon: Briefcase, title: 'Employers', type: 'Employer', color: 'from-teal-500 to-teal-600', description: 'Autism-friendly workplaces' },
        { icon: GraduationCap, title: 'Training Providers', type: 'Training Provider', color: 'from-cyan-500 to-cyan-600', description: 'Skills & vocational programs' },
        { icon: Building2, title: 'Higher Education', type: 'Higher Education', color: 'from-blue-500 to-blue-600', description: 'Colleges & universities' },
        { icon: Users, title: 'Support Organizations', type: 'Support Organization', color: 'from-green-500 to-green-600', description: 'Community & advocacy' },
        { icon: Truck, title: 'Transportation', type: 'Transportation', color: 'from-indigo-500 to-indigo-600', description: 'Accessible transit services' },
    ];

    return (
        <div className="min-h-screen" data-testid="home-page">
            {/* Hero Section */}
            <section className="relative overflow-hidden bg-gradient-to-br from-teal-50 via-background to-background">
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMxNEI4QTYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')] opacity-50" />
                
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 relative">
                    <div className="max-w-3xl mx-auto text-center">
                        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6" data-testid="hero-heading">
                            Helping Adults with Autism{' '}
                            <span className="bg-gradient-to-r from-teal-600 via-cyan-500 to-blue-500 bg-clip-text text-transparent">Perform and Excel</span>
                        </h1>
                        <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto" data-testid="hero-tagline">
                            Opening doors to meaningful careers for neurodivergent adults while delivering exceptional talent for employers
                        </p>

                        <div className="flex flex-wrap gap-3 justify-center">
                            <Link to="/members">
                                <Button size="lg" className="gap-2" data-testid="hero-members-btn">
                                    <Users className="h-4 w-4" />
                                    Member Directory
                                </Button>
                            </Link>
                            <Link to="/apply">
                                <Button variant="ghost" size="lg" className="gap-2">
                                    Apply For Membership
                                    <ChevronRight className="h-4 w-4" />
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Workforce Ecosystem Chart */}
            <section className="py-16" data-testid="ecosystem-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col items-center">
                        <h2 className="font-heading text-3xl font-bold mb-2 text-center">
                            The HAAPE Workforce Ecosystem
                        </h2>
                        <p className="text-muted-foreground mb-8 text-center max-w-2xl">
                            Connecting employers, candidates, education systems, and support organizations to create inclusive career pathways
                        </p>
                        <div className="max-w-2xl w-full">
                            <img 
                                src="https://customer-assets.emergentagent.com/job_8d7f2861-5b7b-44f0-98a1-ebfaa78ebb7b/artifacts/mld2b5ko_HAAPE%27s%20Workforce%20Ecosystem%20Chart%20%281%29.png"
                                alt="HAAPE's Workforce Ecosystem - connecting Employers, Candidates, Parents and Caregivers, Education Systems, Service and Support Organizations, Transportation, and Funders"
                                className="w-full rounded-2xl shadow-lg border border-border"
                                data-testid="ecosystem-chart"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Mission & Vision */}
            <section className="py-16 bg-muted/30" data-testid="mission-vision-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-2 gap-8">
                        <Card className="border-teal-200 dark:border-teal-800 bg-gradient-to-br from-teal-50/50 to-background dark:from-teal-950/30">
                            <CardContent className="p-8">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-10 h-10 rounded-lg bg-teal-500 flex items-center justify-center">
                                        <Target className="h-5 w-5 text-white" />
                                    </div>
                                    <h3 className="font-heading text-xl font-bold">Our Mission</h3>
                                </div>
                                <p className="text-muted-foreground leading-relaxed">
                                    The mission of HAAPE is to create autism workforce ecosystems focused on training and education 
                                    that empower autistic individuals to obtain meaningful and sustainable careers.
                                </p>
                            </CardContent>
                        </Card>

                        <Card className="border-blue-200 dark:border-blue-800 bg-gradient-to-br from-blue-50/50 to-background dark:from-blue-950/30">
                            <CardContent className="p-8">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center">
                                        <Globe2 className="h-5 w-5 text-white" />
                                    </div>
                                    <h3 className="font-heading text-xl font-bold">Our Vision</h3>
                                </div>
                                <p className="text-muted-foreground leading-relaxed">
                                    HAAPE is committed to creating a world in which all autistic individuals can live 
                                    an independent and fulfilling life.
                                </p>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </section>

            {/* Member Directory — Most Prominent Section */}
            <section className="py-20 bg-gradient-to-b from-background to-primary/5" data-testid="member-directory-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-10">
                        <Badge variant="secondary" className="mb-4 px-3 py-1">
                            <Users className="h-3 w-3 mr-1" />
                            HAAPE Network
                        </Badge>
                        <h2 className="font-heading text-3xl md:text-4xl font-bold mb-3">
                            Member Directory
                        </h2>
                        <p className="text-muted-foreground max-w-2xl mx-auto">
                            Explore our network of trusted partner organizations, each committed to advancing 
                            sustainable employment and empowerment for autistic adults
                        </p>
                    </div>

                    {/* Impact Metrics Row — hidden until real data is flowing
                    {impactData && (
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-10" data-testid="member-impact-metrics">
                            ...
                        </div>
                    )}
                    */}

                    {/* Category Cards — Browse by Organization Type */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5 mb-10">
                        {memberCategories.map((category) => (
                            <Link 
                                key={category.type}
                                to={`/members?type=${encodeURIComponent(category.type)}`}
                                className="group"
                                data-testid={`member-category-${category.type.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                                <Card className="h-full border-0 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden">
                                    <CardContent className="p-0">
                                        <div className={`bg-gradient-to-br ${category.color} p-5 flex items-center justify-center`}>
                                            <category.icon className="h-8 w-8 text-white group-hover:scale-110 transition-transform duration-300" />
                                        </div>
                                        <div className="p-4 text-center">
                                            <h3 className="font-heading text-sm font-bold mb-1 group-hover:text-primary transition-colors duration-200">
                                                {category.title}
                                            </h3>
                                            <p className="text-xs text-muted-foreground">{category.description}</p>
                                        </div>
                                    </CardContent>
                                </Card>
                            </Link>
                        ))}
                    </div>

                    {/* CTA to Member Directory */}
                    <div className="text-center">
                        <Link to="/members">
                            <Button size="lg" className="gap-2">
                                <Users className="h-4 w-4" />
                                View Full Member Directory
                                <ArrowRight className="h-4 w-4" />
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* What You'll Find Section */}
            <section className="py-16" data-testid="what-youll-find-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="font-heading text-3xl font-bold mb-3">
                            What You'll Find in the HAAPE Hub
                        </h2>
                        <p className="text-muted-foreground max-w-2xl mx-auto">
                            A warm, welcoming space where our partner organizations, members, and allies come together 
                            to unlock the potential of autistic adults
                        </p>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        <Card className="h-full hover:border-primary/50 hover:shadow-md transition-all duration-200">
                            <CardContent className="p-6">
                                <div className="w-12 h-12 rounded-lg bg-teal-500 flex items-center justify-center mb-4">
                                    <Users className="h-6 w-6 text-white" />
                                </div>
                                <h3 className="font-heading font-semibold mb-2">Member Directory</h3>
                                <p className="text-sm text-muted-foreground">
                                    Connect and collaborate with our HAAPE Certified employers, training providers, and support organizations across the U.S. that share your commitment to neurodivergent employment.
                                </p>
                            </CardContent>
                        </Card>

                        <Card className="h-full hover:border-primary/50 hover:shadow-md transition-all duration-200">
                            <CardContent className="p-6">
                                <div className="w-12 h-12 rounded-lg bg-blue-500 flex items-center justify-center mb-4">
                                    <Lock className="h-6 w-6 text-white" />
                                </div>
                                <h3 className="font-heading font-semibold mb-2">Secure Private Access</h3>
                                <p className="text-sm text-muted-foreground">
                                    A private, vetted community. Log in to access exclusive resources, connect with 
                                    fellow members, and participate in events.
                                </p>
                            </CardContent>
                        </Card>

                        <Card className="h-full hover:border-primary/50 hover:shadow-md transition-all duration-200">
                            <CardContent className="p-6">
                                <div className="w-12 h-12 rounded-lg bg-cyan-500 flex items-center justify-center mb-4">
                                    <BookOpen className="h-6 w-6 text-white" />
                                </div>
                                <h3 className="font-heading font-semibold mb-2">Resource Library</h3>
                                <p className="text-sm text-muted-foreground">
                                    A curated collection of guides, toolkits, and employment resources designed to 
                                    support your journey.
                                </p>
                            </CardContent>
                        </Card>

                        <Card className="h-full hover:border-primary/50 hover:shadow-md transition-all duration-200">
                            <CardContent className="p-6">
                                <div className="w-12 h-12 rounded-lg bg-amber-500 flex items-center justify-center mb-4">
                                    <FolderSearch className="h-6 w-6 text-white" />
                                </div>
                                <h3 className="font-heading font-semibold mb-2">Public Directory</h3>
                                <p className="text-sm text-muted-foreground">
                                    A searchable directory of U.S. organizations, groups, and services dedicated to the neurodivergent community.
                                </p>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </section>

            {/* Chapters */}
            {chapters.length > 0 && (
            <section className="py-16 bg-primary/5">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="font-heading text-3xl font-bold mb-3">
                            Chapters
                        </h2>
                        <p className="text-muted-foreground max-w-2xl mx-auto">
                            We're actively expanding our ecosystem in these key growth markets
                        </p>
                    </div>

                    <div className={`grid sm:grid-cols-2 ${chapters.length >= 4 ? 'lg:grid-cols-4' : chapters.length === 3 ? 'lg:grid-cols-3' : ''} gap-4`}>
                        {chapters.map((chapter) => (
                            <Link
                                key={chapter.chapter_id}
                                to={`/directory?state=${encodeURIComponent(chapter.state)}`}
                                className="group"
                                data-testid={`chapter-link-${chapter.chapter_id}`}
                            >
                                <Card className="hover:border-primary/50 hover:shadow-md transition-all duration-200">
                                    <CardContent className="p-5 flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center group-hover:bg-amber-200 transition-colors duration-200">
                                            <Shield className="h-5 w-5 text-amber-600" />
                                        </div>
                                        <div>
                                            <h3 className="font-heading font-semibold group-hover:text-primary transition-colors duration-200">
                                                {chapter.name}
                                            </h3>
                                            <p className="text-xs text-muted-foreground">{chapter.state}</p>
                                        </div>
                                    </CardContent>
                                </Card>
                            </Link>
                        ))}
                    </div>
                    
                    <div className="mt-8 text-center">
                        <Link to="/map">
                            <Button variant="outline" size="lg" className="gap-2">
                                <Map className="h-5 w-5" />
                                Explore Interactive Map
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>
            )}

            {/* Trusted Partners */}
            {partners.length > 0 && (
            <section className="py-16 bg-slate-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-10">
                        <Badge variant="outline" className="mb-3 text-primary border-primary/30">
                            <Handshake className="h-3 w-3 mr-1" />
                            Curated Access
                        </Badge>
                        <h2 className="font-heading text-3xl font-bold mb-3">
                            Trusted Partner Databases
                        </h2>
                        <p className="text-muted-foreground max-w-2xl mx-auto">
                            Explore leading databases from our credentialed partner organizations — expanding the resources available to the neurodivergent community
                        </p>
                    </div>
                    <div className={`grid ${partners.length === 1 ? 'max-w-xl mx-auto' : 'sm:grid-cols-2'} gap-6`}>
                        {partners.map((partner) => (
                            <Link
                                key={partner.partner_id}
                                to="/partners"
                                className="group"
                                data-testid={`home-partner-${partner.partner_id}`}
                            >
                                <Card className="h-full border-2 hover:border-primary/40 hover:shadow-lg transition-all duration-200 overflow-hidden">
                                    <CardContent className="p-6">
                                        <div className="flex items-start gap-4">
                                            <div className="p-3 rounded-xl bg-primary/10 shrink-0">
                                                <Globe2 className="h-6 w-6 text-primary" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <h3 className="font-bold text-base group-hover:text-primary transition-colors">{partner.name}</h3>
                                                {partner.organization && partner.organization !== partner.name && (
                                                    <p className="text-xs text-muted-foreground mt-0.5">{partner.organization}</p>
                                                )}
                                                <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{partner.description}</p>
                                                <div className="flex flex-wrap gap-1.5 mt-3">
                                                    {(partner.databases || []).map((db) => (
                                                        <Badge key={db.db_id} variant="secondary" className="text-xs">
                                                            {db.name}
                                                        </Badge>
                                                    ))}
                                                </div>
                                            </div>
                                            <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all shrink-0 mt-1" />
                                        </div>
                                    </CardContent>
                                </Card>
                            </Link>
                        ))}
                    </div>
                    <div className="mt-8 text-center">
                        <Link to="/partners">
                            <Button variant="outline" size="lg" className="gap-2">
                                <FolderSearch className="h-5 w-5" />
                                Explore All Partner Databases
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>
            )}

            {/* CTA Section */}
            <section className="py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <Card className="bg-primary text-primary-foreground overflow-hidden relative">
                        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNGRkZGRkYiIGZpbGwtb3BhY2l0eT0iMC4xIj48Y2lyY2xlIGN4PSIzMCIgY3k9IjMwIiByPSIyIi8+PC9nPjwvZz48L3N2Zz4=')] opacity-50" />
                        <CardContent className="p-8 md:p-12 relative">
                            <div className="max-w-2xl">
                                <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                                    Know a Great Resource?
                                </h2>
                                <p className="text-primary-foreground/80 mb-6 text-lg">
                                    Help us grow the directory by sharing organizations that 
                                    support autistic well-being or employment. All submissions 
                                    are reviewed by our team.
                                </p>
                                <div className="flex flex-wrap gap-3">
                                    <Link to="/submit">
                                        <Button 
                                            size="lg" 
                                            variant="secondary"
                                            className="gap-2"
                                            data-testid="cta-submit-btn"
                                        >
                                            Submit a Resource
                                            <ArrowRight className="h-4 w-4" />
                                        </Button>
                                    </Link>
                                    <Link to="/apply">
                                        <Button 
                                            size="lg" 
                                            variant="outline"
                                            className="gap-2 bg-transparent border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10"
                                            data-testid="cta-apply-btn"
                                        >
                                            Apply For Membership
                                            <ArrowRight className="h-4 w-4" />
                                        </Button>
                                    </Link>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </section>
        </div>
    );
};

export default HomePage;
